if (!customElements.get('azantic-collection')) {
  class AzanticCollection extends HTMLElement {
    constructor() {
      super();
      this.loadMoreButton = null;
      this.productGrid = null;
      this.currentPage = 1;
      this.itemsPerPage = 16;
      this.collectionHandle = '';
      this.filtersModal = null;
      this.openFiltersButton = null;
      this.closeFiltersButton = null;
      this.filtersContainer = null;
      this.selectedFilters = new URLSearchParams(window.location.search);
      this.filtersButtonLabel = null;
      this.productsViewed = null;
      this.totalProducts = null;
      this.loadingBar = null;
      this.productsLoaded = 0;
    }

    connectedCallback() {
      this.loadMoreButton = this.querySelector('#azantic-load-more');
      this.productGrid = this.querySelector('#azantic-product-grid');
      this.collectionHandle = this.getAttribute('data-collection-handle');
      this.itemsPerPage = parseInt(this.getAttribute('data-items-per-page')) || 16;
      this.currentPage = 1;
      this.productsViewed = this.querySelector('#products-viewed');
      this.totalProducts = this.querySelector('#total-products');
      this.loadingBar = this.querySelector('#azantic-loading-bar');
      this.productsLoaded = this.itemsPerPage;

      this.filtersModal = this.querySelector('#azantic-filters-modal');
      this.applyFiltersButton = this.querySelector('#azantic-collection-section__apply-filters');
      this.openFiltersButton = this.querySelector('#azantic-collection-section__open-filters-modal');
      this.closeFiltersButton = this.querySelector('#azantic-close-filters-modal');
      this.filtersContainer = this.querySelector('#azantic-filters-container');
      this.filtersButtonLabel = document.querySelector('.filters-button__label');

      // Event listeners for filters
      this.filtersContainer.addEventListener('change', (e) => {
        if (e.target.type === 'checkbox') {
          this.updateFilters(e.target);
        }
      });

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', () => {
          this.loadMoreProducts();
        });
      }

      if (this.openFiltersButton) {
        this.openFiltersButton.addEventListener('click', () => {
          this.openFiltersModal();
          document.body.classList.add('scroll-disabled');
        });
      }

      if (this.closeFiltersButton) {
        this.closeFiltersButton.addEventListener('click', () => {
          this.closeFiltersModal();
          document.body.classList.remove('scroll-disabled');
        });
      }

      // Close modal when clicking outside of it
      if (this.filtersModal) {
        this.filtersModal.addEventListener('click', (e) => {
          // Close modal only if the user clicks on the background (not the modal content)
          if (e.target === this.filtersModal) {
            this.closeFiltersModal();
            document.body.classList.remove('scroll-disabled');
          }
        });
      }

      if (this.applyFiltersButton) {
        this.applyFiltersButton.addEventListener('click', () => {
          this.closeFiltersModal();
          document.body.classList.remove('scroll-disabled');
        });
      }

      // Initial load of products and check if "Load More" should be displayed
      this.loadProducts();

      this.initFilterButtons();
      this.updateCategoryCounter();
      this.updateFiltersLabel();
      this.initImageHover();
    }

    updateFilters(target) {
      const filterParam = target.name;
      const filterValue = target.value;

      if (target.checked) {
        this.selectedFilters.set(filterParam, filterValue);
      } else {
        this.selectedFilters.delete(filterParam);
      }

      this.updateURL();
      this.loadProducts();
      this.updateCategoryCounter();
      this.updateFiltersLabel();
    }

    updateURL() {
      const newURL = `/collections/${this.collectionHandle}?${this.selectedFilters.toString()}`;
      history.pushState(null, '', newURL);
    }

    loadProducts() {
      const url = `/collections/${this.collectionHandle}?${this.selectedFilters.toString()}&page=1&limit=${this.itemsPerPage}`;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xhr.responseText, 'text/html');
          const newProducts = doc.querySelectorAll('.azantic-collection-section__product');
          this.productGrid.innerHTML = '';
          newProducts.forEach((product) => {
            this.productGrid.appendChild(product);
          });
          this.currentPage = 1;
          
          // Reset products loaded count
          this.productsLoaded = newProducts.length;
          
          // Update product counter
          this.updateProductCounter();
          
          // Update loading bar
          this.updateLoadingBar();

          // Check if "Load More" should be shown after loading products
          this.checkLoadMoreButton(newProducts.length);
          this.initImageHover();
        } else {
          console.error('Failed to load products');
        }
      };

      xhr.onerror = () => {
        console.error('Request failed');
      };

      xhr.send();
    }

    loadMoreProducts() {
      const url = `/collections/${this.collectionHandle}?${this.selectedFilters.toString()}&page=${this.currentPage + 1}&limit=${this.itemsPerPage}`;

      // Show loading state
      this.loadMoreButton.textContent = 'LOADING...';
      this.loadMoreButton.disabled = true;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xhr.responseText, 'text/html');
          const newProducts = doc.querySelectorAll('.azantic-collection-section__product');

          if (newProducts.length > 0) {
            newProducts.forEach((product) => {
              this.productGrid.appendChild(product);
            });
            this.currentPage++;
            
            // Update products loaded count
            this.productsLoaded += newProducts.length;
            
            // Update product counter
            this.updateProductCounter();
            
            // Update loading bar
            this.updateLoadingBar();

            // Check if "Load More" should be shown after loading more products
            this.checkLoadMoreButton(newProducts.length);
          } else {
            this.hideLoadMoreButton();
          }
          
          // Reset button state
          this.loadMoreButton.textContent = 'LOAD MORE';
          this.loadMoreButton.disabled = false;
        } else {
          console.error('Failed to load more products');
          this.loadMoreButton.textContent = 'LOAD MORE';
          this.loadMoreButton.disabled = false;
        }
      };

      xhr.onerror = () => {
        console.error('Request failed');
        this.loadMoreButton.textContent = 'LOAD MORE';
        this.loadMoreButton.disabled = false;
      };

      xhr.send();
    }

    checkLoadMoreButton(loadedProductsCount = 0) {
      // Check if there are more products to load
      const url = `/collections/${this.collectionHandle}?${this.selectedFilters.toString()}&page=${this.currentPage + 1}&limit=${this.itemsPerPage}`;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 400) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(xhr.responseText, 'text/html');
          const newProducts = doc.querySelectorAll('.azantic-collection-section__product');

          // If no new products are found or loadedProductsCount is less than itemsPerPage, hide the "Load More" button
          if (newProducts.length === 0 || loadedProductsCount < this.itemsPerPage) {
            this.hideLoadMoreButton();
          } else {
            this.showLoadMoreButton();
          }
        }
      };

      xhr.onerror = () => {
        console.error('Request failed');
      };

      xhr.send();
    }

    hideLoadMoreButton() {
      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = 'none';
      }
    }
    
    updateProductCounter() {
      if (this.productsViewed && this.totalProducts) {
        const totalProductsCount = parseInt(this.totalProducts.textContent);
        this.productsViewed.textContent = `1 - ${Math.min(this.productsLoaded, totalProductsCount)}`;
      }
    }
    
    updateLoadingBar() {
      if (this.loadingBar && this.totalProducts) {
        const totalProductsCount = parseInt(this.totalProducts.textContent);
        const percentage = Math.min((this.productsLoaded / totalProductsCount) * 100, 100);
        this.loadingBar.style.width = `${percentage}%`;
      }
    }

    showLoadMoreButton() {
      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = 'block';
      }
    }

    openFiltersModal() {
      this.filtersModal.classList.remove('azantic-collection-section__filters-modal--hidden');
    }

    closeFiltersModal() {
      this.filtersModal.classList.add('azantic-collection-section__filters-modal--hidden');
    }

    initFilterButtons() {
      const listItems = document.querySelectorAll('.list-menu__item');

      listItems.forEach((listItem) => {
        listItem.addEventListener('click', () => {
          const checkbox = listItem.querySelector('input[type="checkbox"]');

          if (checkbox) {
            checkbox.checked = !checkbox.checked;
            this.updateFilters(checkbox);
          }
        });
      });
    }

    updateCategoryCounter() {
      const filterSections = document.querySelectorAll('[id^="facets-wrapper__filters-set--"]');
      filterSections.forEach(section => {
        const checkboxes = section.querySelectorAll('input[type="checkbox"]');
        const activeCheckboxes = Array.from(checkboxes).filter(checkbox => checkbox.checked);
        const activeCount = activeCheckboxes.length;
        const counterSpan = section.querySelector('.facets-label__counter');
        if (counterSpan) {
          if (activeCount === 0) {
            counterSpan.textContent = '';
          } else {
            counterSpan.textContent = activeCount < 10 ? `0${activeCount}` : `${activeCount}`;
          }
        }
      });
    }

    updateFiltersLabel() {
      if (!this.filtersButtonLabel) return;

      const hasActiveFilters = Array.from(this.filtersContainer.querySelectorAll('input[type="checkbox"]')).some(checkbox => checkbox.checked);

      if (hasActiveFilters) {
        this.filtersButtonLabel.classList.add('filters--active');
      } else {
        this.filtersButtonLabel.classList.remove('filters--active');
      }
    }

    initImageHover() {
      const productCards = document.querySelectorAll('.azantic-collection-section__product');

      productCards.forEach((productCard) => {
        let innerCard = productCard.querySelector('.azantic-product-card-gallery');

        innerCard.addEventListener('mouseenter', () => {
          innerCard.setAttribute('data-state', 'open');
        });

        innerCard.addEventListener('mouseleave', () => {
          innerCard.setAttribute('data-state', 'close');
        });
      });
    }
  }

  customElements.define('azantic-collection', AzanticCollection);
}

// Carousel
if (!customElements.get('azantic-product-media')) {
  class AzanticProductMedia extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.items = [];
      this.prevButton = null;
      this.nextButton = null;
      this.currentIndex = 0;
      this.isDragging = false;
      this.startPos = 0;
      this.currentTranslate = 0;
      this.prevTranslate = 0;

      // Prevent drag on the entire card
      const productCard = this.closest('.azantic-product-card-gallery');
      if (productCard) {
        productCard.addEventListener('dragstart', (e) => e.preventDefault());

        productCard.addEventListener('mouseenter', () => {
          if (!this.isDragging) {
            productCard.setAttribute('data-state', 'open');
            this.currentIndex = 1;
            const width = this.items[0].getBoundingClientRect().width;
            this.currentTranslate = -width;
            this.prevTranslate = this.currentTranslate;
            this.setTransform(this.currentTranslate);
          }
        });

        productCard.addEventListener('mouseleave', () => {
          if (!this.isDragging) {
            productCard.setAttribute('data-state', 'close');
            this.currentIndex = 0;
            this.currentTranslate = 0;
            this.prevTranslate = 0;
            this.setTransform(0);
          }
        });
      }
    }

    connectedCallback() {
      this.track = this.querySelector('.carousel-track');
      this.items = Array.from(this.track?.children || []);
      this.prevButton = this.querySelector('.product-card-gallery__arrow--left');
      this.nextButton = this.querySelector('.product-card-gallery__arrow--right');

      // Prevent drag on images
      this.items.forEach(item => {
        item.addEventListener('dragstart', (e) => e.preventDefault());
      });

      this.setTransform(0);
      this.initCarousel();
      this.initDragEvents();
    }

    initCarousel() {
      if (!this.track || this.items.length === 0) return;

      const handleClick = (direction) => (event) => {
        event.stopPropagation();
        event.preventDefault();

        const newIndex = this.currentIndex + direction;
        if (newIndex >= 0 && newIndex < this.items.length) {
          this.currentIndex = newIndex;
          const width = this.items[0].getBoundingClientRect().width;
          this.currentTranslate = -(this.currentIndex * width);
          this.prevTranslate = this.currentTranslate;
          this.setTransform(this.currentTranslate);
        }
      };

      if (this.nextButton) {
        this.nextButton.addEventListener('click', handleClick(1));
      }

      if (this.prevButton) {
        this.prevButton.addEventListener('click', handleClick(-1));
      }
    }

    initDragEvents() {
      if (!this.track) return;

      // Mouse Events
      this.track.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.isDragging = true;
        this.startPos = e.clientX;
        this.track.style.cursor = 'grabbing';
      });

      document.addEventListener('mousemove', (e) => {
        if (!this.isDragging) return;

        e.preventDefault();
        const currentPosition = e.clientX;
        const diff = currentPosition - this.startPos;
        this.currentTranslate = this.prevTranslate + diff;
        this.setTransform(this.currentTranslate);
      });

      document.addEventListener('mouseup', () => {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.track.style.cursor = 'grab';
        this.finalizeDrag();
      });

      // Touch Events
      this.track.addEventListener('touchstart', (e) => {
        this.isDragging = true;
        this.startPos = e.touches[0].clientX;
      }, { passive: true });

      document.addEventListener('touchmove', (e) => {
        if (!this.isDragging) return;

        const currentPosition = e.touches[0].clientX;
        const diff = currentPosition - this.startPos;
        this.currentTranslate = this.prevTranslate + diff;
        this.setTransform(this.currentTranslate);
      }, { passive: true });

      document.addEventListener('touchend', () => {
        if (!this.isDragging) return;

        this.isDragging = false;
        this.finalizeDrag();
      });
    }

    finalizeDrag() {
      const movedBy = this.currentTranslate - this.prevTranslate;
      const width = this.items[0].getBoundingClientRect().width;

      if (Math.abs(movedBy) > width * 0.3) {
        if (movedBy < 0 && this.currentIndex < this.items.length - 1) {
          this.currentIndex++;
        } else if (movedBy > 0 && this.currentIndex > 0) {
          this.currentIndex--;
        }
      }

      this.currentTranslate = -(this.currentIndex * width);
      this.prevTranslate = this.currentTranslate;
      this.setTransform(this.currentTranslate);
    }

    setTransform(translate) {
      if (!this.track) return;
      this.track.style.transition = this.isDragging ? 'none' : 'transform 0.3s ease-out';
      this.track.style.transform = `translateX(${translate}px)`;
    }
  }

  customElements.define('azantic-product-media', AzanticProductMedia);
}
